import { useState, type JSX } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Box, Checkbox, Label, Select, SelectOption, Text, useToast } from '@wistia/ui';
import type { MediaLocalization, TimeCodedTranscript } from '~/graphql';
import { logMediaEvent } from '~/utilities/logEvent';
import uniqBy from 'lodash/uniqBy';
import uniq from 'lodash/uniq';
import {
  isNonEmptyString,
  isNotNil,
  isNonEmptyArray,
  isNonEmptyRecord,
} from '~/utilities/type-guards';
import type { DownloadableMediaFile } from '~/singlePageApp/components/DownloadModal';
import { useBulkDownloadRecordStreamsMutation } from '~/singlePageApp/screens/Media/MediaSidePanel/__gql/BulkDownloadRecordStreams.gql.gen';
import {
  useNotificationStore,
  getNotificationStoreActions,
} from '~/components/NotificationTray/NotificationStore';
import { trackStructEvent } from '~/utilities/snowplow';
import {
  TabWrapper,
  SelectWrapper,
  CheckboxWrapper,
  DownloadButton,
} from './SharedStyledComponents';
import { addDownloadActionToNotificationTray } from '../BulkActionBar/shared/helpers';

const mp4DerivativeNames = new Set(['224p', '360p', '720p', '1080p', '1440p', '2160p', '4k']);

type Props = {
  mediaId: string;
  mediaHashedId: string;
  defaultIetfLanguageTag: string;
  downloadableMediaFiles: DownloadableMediaFile[];
  localizations: MediaLocalization[];
  captionableTranscripts: TimeCodedTranscript[];
  hasIsos: boolean;
};

export const VideoTab = ({
  mediaId,
  mediaHashedId,
  defaultIetfLanguageTag,
  downloadableMediaFiles,
  localizations,
  captionableTranscripts,
  hasIsos,
}: Props): JSX.Element => {
  const showToast = useToast();
  const [downloadStreams] = useBulkDownloadRecordStreamsMutation();
  const { addTaskToQueue } = useNotificationStore(getNotificationStoreActions);
  const [selectedIsos, setSelectedIsos] = useState(false);
  const [includeCaptions, setIncludeCaptions] = useState(false);
  const [showDownloadMessage, setShowDownloadMessage] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(defaultIetfLanguageTag);

  const downloadsByLanguage = localizations.reduce<Record<string, DownloadableMediaFile[]>>(
    (acc, localization: MediaLocalization) => {
      const downloads = localization.localizedMedia?.downloadableMediaFiles
        ?.nodes as DownloadableMediaFile[];

      if (isNonEmptyArray(downloads)) {
        acc[localization.ietfLanguageTag] = downloads;
      }

      return acc;
    },
    { [defaultIetfLanguageTag]: downloadableMediaFiles },
  );

  const downloadables = downloadsByLanguage[selectedLanguage];

  const localizationLanguages = localizations.map(({ languageName, ietfLanguageTag }) => ({
    label: languageName,
    value: ietfLanguageTag,
  }));

  const transcriptLanguages = captionableTranscripts.map(({ englishName, ietfLanguageTag }) => ({
    label: englishName,
    value: ietfLanguageTag,
  }));

  const languages = uniqBy([...localizationLanguages, ...transcriptLanguages], 'value').filter(
    (language) => isNonEmptyArray(downloadsByLanguage[language.value]),
  );

  const originalFile = downloadables.find(({ displayName }) => displayName === 'Original File');

  const mp4s = downloadables.filter(
    ({ extension, width, height, displayName }) =>
      mp4DerivativeNames.has(displayName) || (extension === 'mp4' && width > 0 && height > 0),
  );

  // mp4 not available right away for record so need to cast type to `| undefined`
  const highestResMp4 = mp4s.sort((vid1, vid2) => vid2.width - vid1.width)[0] as
    | DownloadableMediaFile
    | undefined;

  const [selectedFormat, setSelectedFormat] = useState(
    isNonEmptyArray(mp4s) ? 'mp4' : 'uncompressed',
  );

  const [selectedResolution, setSelectedResolution] = useState(
    highestResMp4?.displayName ?? 'Original File',
  );

  const selectedUncompressed = selectedFormat === 'uncompressed';
  const selectedDubbedVideo = selectedLanguage !== defaultIetfLanguageTag;

  const changeResolution = (resolution: string) => setSelectedResolution(resolution);
  const changeCaptions = () => setIncludeCaptions(!includeCaptions);

  const changeLanguage = (language: string) => {
    setSelectedLanguage(language);

    if (language !== defaultIetfLanguageTag) {
      setIncludeCaptions(false);
    }
  };

  const changeType = (type: string) => {
    const isIsos = type === 'isos';
    setSelectedIsos(isIsos);

    if (isIsos) {
      void logMediaEvent({
        action: 'download',
        hashedId: mediaHashedId,
        displayName: 'Isolated files',
      });
    }
  };

  const changeFormat = (format: string) => {
    setSelectedFormat(format);

    if (format === 'uncompressed') {
      setIncludeCaptions(false);
      setSelectedResolution('Original File');
    } else {
      setSelectedResolution(highestResMp4?.displayName ?? '');
    }
  };

  const selectedFile = downloadsByLanguage[selectedLanguage].find(
    ({ displayName }) => selectedResolution === displayName,
  );

  const selectedFileMediaHashedId = selectedFile?.mediaHashedId;

  const availableCaptions = captionableTranscripts.filter(
    ({ media }) => isNonEmptyRecord(media) && media.hashedId === selectedFileMediaHashedId,
  );

  const downloadDerivative = () => {
    const downloadUrl =
      includeCaptions && isNonEmptyArray(availableCaptions)
        ? `${selectedFile?.captionedDownloadUrl}&media_id=${selectedFileMediaHashedId}&${availableCaptions
            .map(
              ({ hashedId, ietfLanguageTag, activeVersion }) =>
                `transcript=${hashedId}-${ietfLanguageTag}-${activeVersion}`,
            )
            .join('&')}`
        : selectedFile?.downloadUrl;

    if (isNonEmptyString(downloadUrl)) {
      window.open(downloadUrl, '_blank');

      if (selectedDubbedVideo) {
        trackStructEvent({
          category: 'button',
          action: 'click',
          label: 'publish-download-dubbed-video',
          property: 'language',
          value: selectedLanguage,
        });

        return;
      }

      if (selectedUncompressed) {
        trackStructEvent({
          category: 'button',
          action: 'click',
          label: 'publish-download-uncompressed-video',
        });
      } else {
        trackStructEvent({
          category: 'button',
          action: 'click',
          label: 'publish-download-encoded-video',
          property: 'resolution-and-captions',
          value: `${selectedResolution}-${includeCaptions ? 'captions' : 'no-captions'}`,
        });
      }
    } else {
      // eslint-disable-next-line no-console
      console.error('No download URL found for selected file', selectedFile);
      showToast({
        message: 'Error downloading video',
        colorScheme: 'error',
      });
    }
  };

  const downloadIsos = async () => {
    const result = await downloadStreams({ variables: { input: { mediaId } } });
    const jobId = result.data?.bulkDownloadRecordStreams?.backgroundJobStatus.id;

    if (isNotNil(jobId)) {
      //  We're downloading the individual streams for one media, so we provide a selection count of 1
      addDownloadActionToNotificationTray({
        addTaskToQueue,
        backgroundJobStatusId: jobId,
        selectionCount: 1,
      });

      trackStructEvent({
        category: 'button',
        action: 'click',
        label: 'publish-download-isolated-feeds',
      });

      setShowDownloadMessage(true);

      setTimeout(() => {
        setShowDownloadMessage(false);
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      }, 3000);
    }
  };

  const handleDownload = () => {
    if (selectedIsos) {
      void downloadIsos();
    } else {
      downloadDerivative();
    }
  };

  return (
    <TabWrapper>
      {hasIsos && (
        <SelectWrapper>
          <Label>Video type</Label>
          <Select
            onChange={changeType}
            value={selectedIsos ? 'isos' : 'derivatives'}
            fullWidth={true}
          >
            <SelectOption value="isos">Individual feeds as .zip</SelectOption>
            <SelectOption value="derivatives">Final video</SelectOption>
          </Select>
        </SelectWrapper>
      )}

      {languages.length > 1 && (
        <SelectWrapper>
          <Label>Language</Label>
          <Select
            onChange={changeLanguage}
            value={selectedLanguage}
            fullWidth={true}
            disabled={selectedIsos}
            data-testid="language-select"
          >
            {languages.map(({ label, value }) => (
              <SelectOption
                key={value}
                value={value}
              >
                {label} {value === defaultIetfLanguageTag ? '(original)' : '(dubbed)'}
              </SelectOption>
            ))}
          </Select>
        </SelectWrapper>
      )}

      <SelectWrapper>
        <Label>Format</Label>
        <Select
          onChange={changeFormat}
          value={selectedFormat}
          fullWidth={true}
          disabled={selectedIsos}
        >
          <SelectOption value="uncompressed">Uncompressed</SelectOption>
          <SelectOption value="mp4">mp4</SelectOption>
        </Select>
      </SelectWrapper>

      <SelectWrapper>
        <Label>Resolution</Label>
        <Select
          onChange={changeResolution}
          value={selectedResolution}
          fullWidth={true}
          disabled={selectedUncompressed || selectedIsos}
        >
          {selectedFormat === 'mp4' ? (
            mp4s.map(({ displayName, formattedFilesize }) => (
              <SelectOption
                key={displayName}
                value={displayName}
              >
                {`${displayName} (${formattedFilesize})`}
              </SelectOption>
            ))
          ) : (
            <SelectOption value="Original File">
              Original ({originalFile?.formattedFilesize})
            </SelectOption>
          )}
        </Select>
      </SelectWrapper>

      {isNonEmptyArray(availableCaptions) && (
        <CheckboxWrapper>
          <Checkbox
            label={`Include available captions (${uniq(availableCaptions.map(({ englishName }) => englishName)).join(', ')})`}
            onChange={changeCaptions}
            checked={includeCaptions}
            data-testid="include-captions"
            disabled={selectedUncompressed || selectedIsos}
          />
        </CheckboxWrapper>
      )}

      <Box
        justifyContent="space-between"
        alignItems="center"
        style={{ width: '100%' }}
      >
        {selectedIsos && showDownloadMessage ? (
          <Text variant="body4">We&apos;ll email you when your download is ready!</Text>
        ) : null}

        <DownloadButton
          onClick={handleDownload}
          data-testid="download-video"
        >
          {selectedIsos ? 'Generate .zip' : 'Download'}
        </DownloadButton>
      </Box>
    </TabWrapper>
  );
};
